<template>
	<div name="capture" :id="id" class="print-section investment-analysis">
		<div class="print-tit">INVESTMENT ANALYSIS</div>

		<div class="ia-block-tit"><i></i>실제 들어가는 돈 (매입비용) <small>· 매매가 외 취득세·부대비용 포함</small></div>
		<div class="ia-two-col">
			<table class="ia-table">
				<thead><tr><th>항목</th><th>산정근거</th><th class="num">금액</th></tr></thead>
				<tbody>
					<tr><td>매매금액</td><td class="basis">AI추정가·협의 매매가</td><td class="num">{{ money(acq.sell) }}</td></tr>
					<tr><td>취득세 ({{ taxRate }}% · {{ residential ? '주택' : '상업용' }})</td><td class="basis">매매가 × {{ taxRate }}%<sup>1</sup></td><td class="num">{{ money(acq.tax) }}</td></tr>
					<tr><td>중개보수 ({{ brokerRate }}%)</td><td class="basis">매매가 × {{ brokerRate }}%<sup>2</sup></td><td class="num">{{ money(acq.broker) }}</td></tr>
					<tr><td>법무·기타</td><td class="basis">등기 부대비용 정액<sup>3</sup></td><td class="num">{{ money(acq.legal) }}</td></tr>
					<tr class="total"><td>총 매입비용</td><td class="basis">매매가 + 부대비용</td><td class="num">{{ money(acq.total) }}</td></tr>
				</tbody>
			</table>
			<table class="ia-table ia-table--loan">
				<thead><tr><th>자금 조달</th><th>산정근거</th><th class="num">금액</th></tr></thead>
				<tbody>
					<tr><td>대출금 (LTV {{ ltv }}%)</td><td class="basis">매매가 × {{ ltv }}%</td><td class="num">{{ money(acq.loan) }}</td></tr>
					<tr><td>취득세·부대비용</td><td class="basis">취득세+중개+법무</td><td class="num">{{ money(acq.addons) }}</td></tr>
					<tr><td>연 대출이자 ({{ loanRate }}%)</td><td class="basis">대출금 × {{ loanRate }}%</td><td class="num">약 {{ money(acq.interest) }}</td></tr>
					<tr class="total"><td>실투자금 (내 돈)</td><td class="basis">총 매입비용 − 대출금</td><td class="num">{{ money(acq.equity) }}</td></tr>
				</tbody>
			</table>
		</div>

		<div class="ia-block-tit"><i></i>수익성 요약 <small>· 대출 {{ ltv }}% · 금리 {{ loanRate }}% · 연 NOI {{ money(noi) }}(추정) 기준</small></div>
		<div class="ia-cards">
			<div class="ia-card ia-card--primary">
				<div class="ia-card-label">실투자금 (내 돈)</div>
				<div class="ia-card-value">약 {{ eok(acq.equity) }}억</div>
				<div class="ia-card-sub">취득 부대비용 포함</div>
			</div>
			<div class="ia-card">
				<div class="ia-card-label">연 세전 현금흐름</div>
				<div class="ia-card-value" :class="acq.cashflow >= 0 ? 'pos' : 'neg'">{{ acq.cashflow >= 0 ? '+' : '-' }}{{ money(Math.abs(acq.cashflow)) }}</div>
				<div class="ia-card-sub">연 NOI − 대출이자</div>
			</div>
			<div class="ia-card">
				<div class="ia-card-label">자기자본 수익률</div>
				<div class="ia-card-value" :class="acq.roe == null ? '' : (acq.roe >= 0 ? 'pos' : 'neg')">{{ acq.roe == null ? '-' : Number(acq.roe).toFixed(1) + '%' }}</div>
				<div class="ia-card-sub">현금흐름 ÷ 실투자금</div>
			</div>
		</div>

		<div class="ia-block-tit"><i></i>인수 프로세스</div>
		<div class="ia-timeline">
			<div class="ia-step" v-for="(st, i) in steps" :key="'st' + i">
				<span class="dot"></span>
				<div class="ia-step-no">{{ i + 1 }}. {{ st.t }}</div>
				<div class="ia-step-sub">{{ st.s }}</div>
			</div>
		</div>

		<div class="page-footer-disclaimer">
			<div class="ia-sources-tit">산정근거 · 출처</div>
			<ol class="ia-sources">
				<li v-for="(s, i) in acqSources" :key="'asrc' + i">{{ s }}</li>
			</ol>
			<p class="ia-disc">※ NOI는 입지 기반 추정치이며 매도인의 운영실적 확인 후 실측치로 확정됩니다. 본 자료는 투자 판단의 근거로 사용될 수 없습니다.</p>
			<div class="ia-legal">
				<p v-for="(line, i) in pageFooterLines" :key="'leg' + i">{{ line }}</p>
			</div>
		</div>
	</div>
</template>

<script>
import { ACQ_SOURCES, PAGE_FOOTER_LINES } from '@/utils/investment.mjs';

export default {
	name: 'InvestmentAnalysisPage',
	props: {
		id: { type: String, default: 'capture_inv' },
		acq: { type: Object, required: true },
		taxRate: { type: Number, default: 4.6 },
		brokerRate: { type: Number, default: 0.9 },
		ltv: { type: Number, default: 60 },
		loanRate: { type: Number, default: 5.0 },
		noi: { type: Number, default: 0 },
		residential: { type: Boolean, default: false },
	},
	data() {
		return {
			acqSources: ACQ_SOURCES,
			pageFooterLines: PAGE_FOOTER_LINES,
			steps: [
				{ t: '현장 임장', s: '실물·운영 확인' },
				{ t: '자료 검토', s: '매출·등기·대장' },
				{ t: '조건 협의', s: '가격·대출 설계' },
				{ t: '계약', s: '계약금·잔금' },
				{ t: '잔금 및 인수', s: '임대 승계' },
			],
		};
	},
	methods: {
		money(v) { return this.$formatMoney(v, this.$MONEY_FORMAT_TYPE.LOAN); },
		eok(man) { return Math.round((Number(man) || 0) / 10000); },
	},
};
</script>

<style scoped>
.investment-analysis { display: flex; flex-direction: column; gap: 14px; }
.print-tit { margin-bottom: 14px; }
.ia-block-tit { font-size: 15px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.ia-block-tit i { width: 4px; height: 16px; background: #9b1c1c; display: inline-block; }
.ia-block-tit small { font-weight: 400; color: #888; font-size: 12px; }
.ia-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.ia-table { width: 100%; border-collapse: collapse; }
.ia-table th { background: #f1ebe9; text-align: left; padding: 9px 10px; font-size: 13px; }
.ia-table th.num { text-align: right; }
.ia-table--loan th { background: #e6b9b3; }
.ia-table td { padding: 8px 10px; border-bottom: 1px solid #eee; font-size: 13px; }
.ia-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
.ia-table td.basis { font-size: 11px; color: #888; line-height: 1.35; }
.ia-table td.basis sup { font-size: 8px; color: #9b1c1c; margin-left: 1px; }
.ia-table tr.total td { font-weight: 700; color: #9b1c1c; border-bottom: none; }
.ia-table tr.total td.basis { font-weight: 400; color: #888; }
.page-footer-disclaimer { margin-top: auto; }
.ia-sources-tit { font-size: 11px; font-weight: 700; color: #555; margin-bottom: 3px; }
.ia-sources { font-size: 10px; color: #888; line-height: 1.5; margin: 0 0 6px; padding-left: 15px; }
.ia-sources li { margin-bottom: 2px; }
.ia-disc { font-size: 10px; color: #888; line-height: 1.5; margin: 0 0 7px; }
.ia-legal { border-top: 1px solid rgba(0, 0, 0, 0.06); padding-top: 5px; }
.ia-legal p { margin: 0; font-size: 7.5px; line-height: 1.45; color: #6b6b6b; letter-spacing: -0.01em; text-indent: -6px; padding-left: 6px; word-break: keep-all; }
.ia-legal p + p { margin-top: 1px; }
.ia-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.ia-card { border: 1px solid #eee; border-radius: 6px; padding: 18px; text-align: center; }
.ia-card--primary { background: #7a1818; color: #fff; border-color: #7a1818; }
.ia-card-label { font-size: 12px; opacity: .85; }
.ia-card-value { font-size: 26px; font-weight: 700; margin: 8px 0; }
.ia-card-value.pos { color: #1f8a4c; }
.ia-card-value.neg { color: #c0392b; }
.ia-card-sub { font-size: 11px; opacity: .7; }
.ia-timeline { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; padding-top: 8px; position: relative; }
.ia-step { text-align: center; position: relative; }
.ia-step .dot { width: 12px; height: 12px; border-radius: 50%; background: #9b1c1c; display: inline-block; margin-bottom: 10px; }
.ia-step-no { font-size: 13px; font-weight: 600; line-height: 1.3; }
.ia-step-sub { font-size: 11px; color: #888; line-height: 1.3; margin-top: 3px; }
</style>
